import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { theme } from '../theme';
import logo from '../assets/logo.png';

const SCROLL_ROADMAP = [
  { num: 1,  id: 'habit',            title: 'Habit',             mandino: true,  description: 'The foundation of all transformation. Every great life is built on this scroll first.' },
  { num: 2,  id: 'love',             title: 'Love',              mandino: true,  description: 'The greatest force available to mankind. Read it and watch how the world responds to you.' },
  { num: 3,  id: 'persistence',      title: 'Persistence',       mandino: true,  description: 'The scroll that separates those who dream from those who achieve.' },
  { num: 4,  id: 'selfEsteem',       title: 'Self-Esteem',       mandino: true,  description: 'Your belief in yourself is the ceiling of everything you will ever accomplish.' },
  { num: 5,  id: 'presence',         title: 'Presence',          mandino: true,  description: 'All power lives in this moment. This scroll returns you to it, again and again.' },
  { num: 6,  id: 'emotionalMastery', title: 'Emotional Mastery', mandino: true,  description: 'Learn to feel deeply and direct what you feel. Emotion is energy — master it.' },
  { num: 7,  id: 'joy',              title: 'Joy',               mandino: true,  description: 'Greet each day with laughter and an open heart. Joy is a discipline, not a gift.' },
  { num: 8,  id: 'growth',           title: 'Growth',            mandino: true,  description: 'Your capacity to grow is endless. This scroll awakens your hunger for becoming more.' },
  { num: 9,  id: 'action',           title: 'Action',            mandino: true,  description: 'Where transformation meets the world. Knowledge without action is just information.' },
  { num: 10, id: 'wisdom',           title: 'Wisdom',            mandino: true,  description: 'The final foundation scroll. Wisdom whispers to those still enough to hear.' },
  { num: null, id: 'confidence',     title: 'Confidence',        mandino: false, description: 'Expand the foundation into who you are becoming.' },
  { num: null, id: 'discipline',     title: 'Discipline',        mandino: false, description: 'The bridge between intention and result.' },
  { num: null, id: 'authenticity',   title: 'Authenticity',      mandino: false, description: 'The courage to be fully, unapologetically yourself.' },
  { num: null, id: 'integrity',      title: 'Integrity',         mandino: false, description: 'Align who you are with what you do. Everything else follows.' },
];

const Section = ({ icon, title, children }) => (
  <div style={sectionStyles.wrap}>
    <div style={sectionStyles.iconRow}>
      <span style={sectionStyles.icon}>{icon}</span>
      <h2 style={sectionStyles.title}>{title}</h2>
    </div>
    {children}
  </div>
);

const sectionStyles = {
  wrap: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '16px',
    padding: '36px 40px',
    marginBottom: '24px',
    border: `1px solid ${theme.colors.bgLight}`,
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '14px',
    marginBottom: '20px',
  },
  icon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  title: {
    color: theme.colors.textPrimary,
    fontSize: '1.5rem',
    fontWeight: '700',
    margin: 0,
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

function JourneyPage() {
  const navigate = useNavigate();
  const [expandedScroll, setExpandedScroll] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session?.user);
    });
  }, []);

  return (
    <div style={styles.container}>

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}>
          {isLoggedIn ? '← Back to Dashboard' : '← Back to Home'}
        </button>
      </div>

      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.heroEyebrow}>YOUR GUIDE TO THE JOURNEY</p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', gap: '12px' }}>
            <img src={logo} alt="My Greatest Self" style={{ height: '52px', width: 'auto' }} onError={(e) => { e.target.style.display = 'none'; }} />
            <h1 style={styles.heroTitle}>My Greatest Self</h1>
          </div>
          <p style={styles.heroSubtitle}>
            A system for personal transformation rooted in the most effective 
            affirmation science available — built on the foundation Og Mandino 
            laid over fifty years ago, and made more powerful by everything 
            we've learned since.
          </p>
        </div>
      </div>

      <div style={styles.content}>

        {/* The Origin Story */}
        <Section icon="📖" title="Where This Began">
          <p style={styles.bodyText}>
            Sixteen years ago, I was working a 9-to-5 job I hadn't chosen so much as 
            fallen into. I was restless. I knew there was more — more I was capable of, 
            more I was meant to do — but I didn't have a path. So I started reading. 
            Business books. Self-improvement books. Everything I could find.
          </p>
          <p style={styles.bodyText}>
            Two books changed everything. The first was <em>Think and Grow Rich</em>. 
            The second was Og Mandino's <em>The Greatest Salesman in the World</em>.
          </p>
          <p style={styles.bodyText}>
            I read Mandino's book and became completely captivated by the scroll system 
            he described — reading each scroll three times daily for thirty days before 
            moving to the next. I followed the system exactly as prescribed. And I 
            started to change.
          </p>
          <p style={styles.bodyText}>
            But as I read, I also noticed something. Mandino wrote his scrolls in 1968, 
            when affirmation theory was in its infancy. The science of how the mind 
            actually absorbs and internalizes new beliefs has advanced enormously since 
            then. His scrolls were beautiful — but they weren't built on modern 
            affirmation principles. I started rewriting them.
          </p>
          <p style={styles.bodyText}>
            Over time, I developed twelve rules that every scroll had to follow to 
            maximize its effect on the subconscious mind. I went on to build a company 
            that grew into a global brand with millions of customers — and eventually 
            sold it. I credit the scroll system, practiced faithfully, as one of the 
            foundations of that journey.
          </p>
          <p style={styles.bodyText}>
            Now I want to share what I built. Not just the original ten scrolls — 
            reimagined for the science we have today — but an expanding library of 
            scrolls covering dozens of human characteristics. This is <strong style={{ color: theme.colors.teal }}>My Greatest Self</strong>.
          </p>
        </Section>

        {/* The Core Promise */}
        <Section icon="🌟" title="What This Program Does">
          <div style={styles.promiseBox}>
            <p style={styles.promiseText}>
              "My Greatest Self is a commitment. Read your scroll three times daily — 
              with emotion, with intention, as if every word is already true — for 30 days 
              before moving on. You will not just think differently. You will become different. 
              Each scroll rewires a specific dimension of who you are at the subconscious level. 
              Over time, you don't need to try to be more patient, more disciplined, more loving. 
              You simply become your greatest self."
            </p>
          </div>
          <p style={styles.bodyText}>
            This is not a motivational app. Motivation is a feeling — it comes and goes. 
            This is a <em>reprogramming system</em>. The scrolls work the way physical 
            training works: consistent repetition over time builds something that lasts. 
            The difference is that instead of building muscle, you are building identity.
          </p>
        </Section>

        {/* Why Scrolls Work */}
        <Section icon="🧠" title="Why This Works — The Science">
          <p style={styles.bodyText}>
            A short eight-word affirmation repeated once in the morning is better than 
            nothing. But it is a fraction as effective as what you're doing here.
          </p>
          <p style={styles.bodyText}>
            The scrolls work for three reasons:
          </p>
          <div style={styles.reasonsList}>
            <div style={styles.reasonItem}>
              <div style={styles.reasonNumber}>01</div>
              <div>
                <strong style={styles.reasonTitle}>Depth of immersion.</strong>
                <p style={styles.reasonText}>
                  Three to five minutes of focused reading, three times daily, creates 
                  a level of subconscious exposure that brief affirmations cannot match. 
                  The mind needs repetition and duration to accept a new belief as true.
                </p>
              </div>
            </div>
            <div style={styles.reasonItem}>
              <div style={styles.reasonNumber}>02</div>
              <div>
                <strong style={styles.reasonTitle}>Modern affirmation science.</strong>
                <p style={styles.reasonText}>
                  Every scroll is written following twelve specific rules: present tense, 
                  positive framing, identity-based language, progressive construction, 
                  emotional resonance, and more. These rules are not stylistic — they are 
                  the difference between an affirmation the mind accepts and one it rejects.
                </p>
              </div>
            </div>
            <div style={styles.reasonItem}>
              <div style={styles.reasonNumber}>03</div>
              <div>
                <strong style={styles.reasonTitle}>The thirty-day threshold.</strong>
                <p style={styles.reasonText}>
                  Neuroscience consistently shows that meaningful neural pathway 
                  restructuring requires sustained repetition over weeks, not days. 
                  Thirty days is not an arbitrary number. It is the minimum effective dose.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* How to Use It */}
        <Section icon="📜" title="How to Use This Program">
          <div style={styles.stepsList}>
            <div style={styles.stepItem}>
              <div style={styles.stepDot} />
              <div>
                <strong style={styles.stepTitle}>Begin with the Scroll of Habit.</strong>
                <p style={styles.stepText}>
                  Every user starts here, regardless of where they feel they need the most 
                  work. This is not arbitrary — the Scroll of Habit builds the very 
                  foundation the entire program rests on. Without it, every other scroll 
                  is less effective. Complete thirty qualifying days before moving forward.
                </p>
              </div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepDot} />
              <div>
                <strong style={styles.stepTitle}>Read three times daily — morning, midday, and evening.</strong>
                <p style={styles.stepText}>
                  Each reading takes three to five minutes. A qualifying day requires at 
                  least two of the three readings. A perfect day is all three. The 
                  morning reading sets your mind for the day. The midday reading 
                  reinforces it when the world has had hours to pull you away. The evening 
                  reading plants it deep as your mind moves toward sleep.
                </p>
              </div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepDot} />
              <div>
                <strong style={styles.stepTitle}>Read with emotion and intention — aloud whenever possible.</strong>
                <p style={styles.stepText}>
                  Reading aloud, slowly, as if you mean every word engages your voice, your body, and your full attention — and that combination is what unlocks the subconscious. Whenever your situation allows it, read aloud. That said, a silent reading done with genuine focus and emotion is far more valuable than no reading at all. Never skip a reading because you can't read aloud. Adapt, and show up.
                </p>
              </div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepDot} />
              <div>
                <strong style={styles.stepTitle}>Stay with each scroll for the full thirty days.</strong>
                <p style={styles.stepText}>
                  You will feel ready to move on before thirty days are complete. That 
                  feeling is normal and it is not a signal to advance. The work happening 
                  in your subconscious mind is not visible to you — it operates below the 
                  surface. Trust the process. Thirty days is the minimum. Honor it.
                </p>
              </div>
            </div>
            <div style={styles.stepItem}>
              <div style={styles.stepDot} />
              <div>
                <strong style={styles.stepTitle}>After Habit, choose your path.</strong>
                <p style={styles.stepText}>
                  The app will suggest the next scroll in Og Mandino's original sequence, 
                  and we encourage you to follow that path — it was designed with intention. 
                  But after completing the Scroll of Habit, you have the freedom to choose 
                  whichever scroll calls to you. Follow your instincts. Your greatest self 
                  knows what it needs.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* The Two Rules */}
        <Section icon="⚡" title="The Two Rules That Make the Difference">
          <div style={styles.rulesGrid}>
            <div style={styles.ruleCard}>
              <div style={styles.ruleNumber}>Rule 1</div>
              <h3 style={styles.ruleTitle}>Don't Rush</h3>
              <p style={styles.ruleText}>
                The program works through accumulation. Each reading adds to the last. 
                Skipping days, reading quickly, or advancing early breaks the chain. 
                Thirty days of consistent practice produces results that thirty days of 
                inconsistent practice cannot. Slow is smooth. Smooth is transformative.
              </p>
            </div>
            <div style={styles.ruleCard}>
              <div style={styles.ruleNumber}>Rule 2</div>
              <h3 style={styles.ruleTitle}>Read Like You Mean It</h3>
              <p style={styles.ruleText}>
                Going through the motions produces nothing. Reading with emotion, conviction, and presence produces everything. Before each reading, take one breath and make a choice: for the next five minutes, every word is true about you. Read aloud when you can — it engages your voice and body in a way silent reading cannot match. When you can't read aloud, read silently with full focus and intention. The method matters less than the sincerity you bring to it.
              </p>
            </div>
          </div>
        </Section>

        {/* The Scroll Roadmap */}
        <Section icon="🗺️" title="The Scroll Library">
          <p style={styles.bodyText}>
            The Foundation Scrolls (1–10) follow Og Mandino's original sequence, 
            reimagined with modern affirmation science. The Expansion Scrolls extend 
            the system into additional dimensions of human character and capability. 
            New scrolls are added regularly.
          </p>

          <div style={styles.roadmapGrid}>
            {SCROLL_ROADMAP.map((scroll) => (
              <div
                key={scroll.id}
                style={{
                  ...styles.roadmapCard,
                  ...(scroll.mandino ? styles.roadmapCardMandino : styles.roadmapCardExpansion),
                }}
                onClick={() => setExpandedScroll(expandedScroll === scroll.id ? null : scroll.id)}
              >
                <div style={styles.roadmapCardHeader}>
                  {scroll.num && (
                    <span style={styles.roadmapNum}>#{scroll.num}</span>
                  )}
                  <span style={styles.roadmapTitle}>{scroll.title}</span>
                  <span style={styles.roadmapChevron}>
                    {expandedScroll === scroll.id ? '▲' : '▼'}
                  </span>
                </div>
                {expandedScroll === scroll.id && (
                  <p style={styles.roadmapDescription}>{scroll.description}</p>
                )}
              </div>
            ))}
          </div>

          <div style={styles.roadmapLegend}>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, backgroundColor: `${theme.colors.blue}40`, border: `1px solid ${theme.colors.blue}` }} />
              <span style={styles.legendText}>Foundation Scrolls (Mandino sequence)</span>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, backgroundColor: `${theme.colors.teal}20`, border: `1px solid ${theme.colors.teal}60` }} />
              <span style={styles.legendText}>Expansion Scrolls</span>
            </div>
          </div>
        </Section>

        {/* CTA */}
        <div style={styles.ctaBox}>
          <h2 style={styles.ctaTitle}>Your greatest self is not a destination.</h2>
          <p style={styles.ctaText}>
            It is the person you are becoming — one reading, one day, one scroll at a time.
          </p>
          <button style={styles.ctaButton} onClick={() => navigate('/dashboard')}>
            Return to Your Journey →
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
    paddingBottom: '80px',
  },
  header: {
    maxWidth: '900px',
    margin: '0 auto 24px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.teal,
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: '0.5714em 0',
  },
  hero: {
    maxWidth: '900px',
    margin: '0 auto 48px',
    textAlign: 'center',
    padding: '48px 20px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(20,184,166,0.08) 100%)',
    borderRadius: '24px',
    border: `1px solid ${theme.colors.bgLight}`,
  },
  heroInner: {
    maxWidth: '680px',
    margin: '0 auto',
  },
  heroEyebrow: {
    color: theme.colors.teal,
    fontSize: '0.6875rem',
    fontWeight: '700',
    letterSpacing: '3px',
    marginBottom: '16px',
  },
  heroTitle: {
    fontSize: '3.25rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '20px',
    lineHeight: '1.1',
  },
  heroSubtitle: {
    color: theme.colors.textSecondary,
    fontSize: '1.125rem',
    lineHeight: '1.7',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  bodyText: {
    color: theme.colors.textSecondary,
    fontSize: '1rem',
    lineHeight: '1.8',
    marginBottom: '16px',
  },
  promiseBox: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(20,184,166,0.12) 100%)',
    border: `2px solid ${theme.colors.blue}40`,
    borderRadius: '12px',
    padding: '28px 32px',
    marginBottom: '24px',
  },
  promiseText: {
    color: theme.colors.textPrimary,
    fontSize: '1.0625rem',
    lineHeight: '1.8',
    fontStyle: 'italic',
    margin: 0,
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginTop: '8px',
  },
  reasonItem: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'flex-start',
  },
  reasonNumber: {
    color: theme.colors.teal,
    fontSize: '0.8125rem',
    fontWeight: '700',
    letterSpacing: '1px',
    flexShrink: 0,
    marginTop: '3px',
    minWidth: '2.1538em',
  },
  reasonTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1rem',
    display: 'block',
    marginBottom: '6px',
  },
  reasonText: {
    color: theme.colors.textSecondary,
    fontSize: '0.9375rem',
    lineHeight: '1.7',
    margin: 0,
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  },
  stepItem: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'flex-start',
  },
  stepDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: theme.colors.gradient,
    flexShrink: 0,
    marginTop: '6px',
  },
  stepTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1rem',
    display: 'block',
    marginBottom: '8px',
  },
  stepText: {
    color: theme.colors.textSecondary,
    fontSize: '0.9375rem',
    lineHeight: '1.7',
    margin: 0,
  },
  rulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
  },
  ruleCard: {
    backgroundColor: theme.colors.bgDark,
    borderRadius: '12px',
    padding: '28px',
    border: `1px solid ${theme.colors.bgLight}`,
  },
  ruleNumber: {
    color: theme.colors.teal,
    fontSize: '0.6875rem',
    fontWeight: '700',
    letterSpacing: '2px',
    marginBottom: '10px',
  },
  ruleTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1.25rem',
    fontWeight: '700',
    marginBottom: '12px',
    margin: '0 0 12px 0',
  },
  ruleText: {
    color: theme.colors.textSecondary,
    fontSize: '0.9375rem',
    lineHeight: '1.7',
    margin: 0,
  },
  roadmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '8px',
    marginTop: '20px',
    marginBottom: '20px',
  },
  roadmapCard: {
    borderRadius: '8px',
    padding: '0.875em 1.125em',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  roadmapCardMandino: {
    backgroundColor: `${theme.colors.blue}15`,
    border: `1px solid ${theme.colors.blue}40`,
  },
  roadmapCardExpansion: {
    backgroundColor: `${theme.colors.teal}08`,
    border: `1px solid ${theme.colors.teal}30`,
  },
  roadmapCardHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  roadmapNum: {
    color: theme.colors.teal,
    fontSize: '0.75rem',
    fontWeight: '700',
    minWidth: '2.3333em',
    flexShrink: 0,
  },
  roadmapTitle: {
    color: theme.colors.textPrimary,
    fontSize: '0.9375rem',
    fontWeight: '600',
    flex: 1,
  },
  roadmapChevron: {
    color: theme.colors.textMuted,
    fontSize: '0.625rem',
    flexShrink: 0,
  },
  roadmapDescription: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    lineHeight: '1.6',
    margin: '10px 0 0 38px',
  },
  roadmapLegend: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '3px',
    flexShrink: 0,
  },
  legendText: {
    color: theme.colors.textSecondary,
    fontSize: '0.8125rem',
  },
  ctaBox: {
    textAlign: 'center',
    padding: '60px 40px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(20,184,166,0.10) 100%)',
    borderRadius: '24px',
    border: `1px solid ${theme.colors.bgLight}`,
  },
  ctaTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1.75rem',
    fontWeight: '700',
    marginBottom: '12px',
  },
  ctaText: {
    color: theme.colors.textSecondary,
    fontSize: '1.0625rem',
    lineHeight: '1.7',
    marginBottom: '32px',
    maxWidth: '500px',
    margin: '0 auto 32px',
  },
  ctaButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '12px',
    padding: '1em 2.25em',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
};

export default JourneyPage;